import { useMemo } from 'react'
import {
  Background, Controls, Handle, Position, ReactFlow, type Edge, type Node, type NodeProps,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { Box, Group, Text } from '@mantine/core'
import { IconPlayerPause } from '@tabler/icons-react'
import type { AtorDoCaminho, MapaDoFluxo, NumerosDoFluxo } from '@/api/chamados'
import { corDoStatus } from '@/components/chamados/StatusBadge'
import {
  ID_ANTERIOR, ID_QUALQUER, NO_ALTURA, NO_LARGURA, arestasDoFluxo, layoutDoFluxo,
} from './layoutDoFluxo'
import { rotuloDoEstado } from './textosDoFluxo'
import { ESTADO_ANTERIOR, QUALQUER_ESTADO, type FluxoVisivel, type Visao } from './visaoDoFluxo'
import type { Selecao } from './DetalheDoFluxo'
import estilos from './DiagramaDoFluxo.module.css'

/** Contínua = pessoa; tracejada = o sistema, sozinho; pontilhada = só o administrador (§3.2). */
const TRACO: Record<AtorDoCaminho, string | undefined> = { pessoa: undefined, sistema: '6 4', admin: '2 4' }

interface DadosDoNo extends Record<string, unknown> {
  rotulo: string
  cor: string
  agora: number
  pausa: boolean
  encerra: boolean
}

function NoDoFluxo({ data }: NodeProps<Node<DadosDoNo>>) {
  return (
    <Box style={{
      width: NO_LARGURA, height: NO_ALTURA, padding: '6px 10px', borderRadius: 8,
      background: 'var(--ai-card-bg)',
      border: `${data.encerra ? 3 : 1}px ${data.encerra ? 'double' : 'solid'} var(--mantine-color-${data.cor}-6)`,
      display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'pointer',
    }}>
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Group gap={4} wrap="nowrap">
        <Text size="sm" fw={600} lh={1.2} lineClamp={2}>{data.rotulo}</Text>
        {data.pausa && <IconPlayerPause size={14} stroke={1.75} aria-label="pausa o prazo" />}
      </Group>
      {data.agora > 0 && <Text size="xs" ff="monospace">{data.agora} agora</Text>}
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </Box>
  )
}

const nodeTypes = { estado: NoDoFluxo }

interface Props {
  mapa: MapaDoFluxo
  fluxo: FluxoVisivel
  numeros?: NumerosDoFluxo
  visao: Visao
  onSelecionar: (s: Selecao) => void
}

export function DiagramaDoFluxo({ mapa, fluxo, numeros, visao, onSelecionar }: Props) {
  const { nodes, edges } = useMemo(() => {
    const arestas = arestasDoFluxo(fluxo.caminhos, fluxo.estados)
    const posicoes = layoutDoFluxo(fluxo.estados, arestas)
    const nodes: Node<DadosDoNo>[] = posicoes.map((p) => {
      const estado = p.id === ID_QUALQUER ? QUALQUER_ESTADO : p.id === ID_ANTERIOR ? ESTADO_ANTERIOR : p.id
      return {
        id: p.id, type: 'estado', position: { x: p.x, y: p.y },
        data: {
          rotulo: rotuloDoEstado(estado, visao),
          cor: estado === p.id ? corDoStatus(estado) : 'gray',
          agora: numeros?.por_estado[estado] ?? 0,
          pausa: mapa.pausam.includes(estado),
          encerra: mapa.encerrados.includes(estado),
        },
      }
    })
    const edges: Edge[] = arestas.map((a) => ({
      id: a.id, source: a.de, target: a.para,
      type: 'default',
      data: { caminho: a.caminho },
      style: { strokeDasharray: TRACO[a.ator], strokeWidth: 1.5 },
      markerEnd: { type: 'arrowclosed' as const },
    }))
    return { nodes, edges }
  }, [mapa, fluxo, numeros, visao])

  return (
    <Box className={estilos.diagrama} style={{ height: 560, border: '1px solid var(--ai-border-light)', borderRadius: 8 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_, n) => {
          if (n.id !== ID_QUALQUER && n.id !== ID_ANTERIOR) onSelecionar({ tipo: 'estado', estado: n.id })
        }}
        onEdgeClick={(_, e) => onSelecionar({ tipo: 'caminho', caminho: (e.data as { caminho: never }).caminho })}
      >
        <Background gap={24} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </Box>
  )
}
